import * as T from 'types'
import * as E from 'types/enums'

import { sortByDateModified } from './listingHelpers'

export const isClosedOrSold = (listing?: T.IListing | T.ISparseListing) => {
  switch (listing?.listingStatus?.name ?? '') {
    case E.ListingStatus.closedNotPaid:
    case E.ListingStatus.sold:
    case E.ListingStatus.leased:
    case E.ListingStatus.escrowClosed:
      return true
    default:
      return false
  }
}

export const isInContract = (listing?: T.IListing | T.ISparseListing) => {
  switch (listing?.listingStatus?.name ?? '') {
    case E.ListingStatus.attorneyReview:
    case E.ListingStatus.inContract:
    case E.ListingStatus.newEscrow:
    case E.ListingStatus.contractUpdated:
      return true
    default:
      return false
  }
}

export const getDashboardDisplayStatus = (listing?: T.IListing | T.ISparseListing) => {
  switch (listing?.listingStatus?.name ?? '') {
    case E.ListingStatus.draft:
      return 'Resume Editing'
    case E.ListingStatus.sold:
    case E.ListingStatus.closedNotPaid:
    case E.ListingStatus.escrowClosed:
      return 'Closed'
    case E.ListingStatus.cancelled:
    case E.ListingStatus.cancellationSigned:
      return 'Cancelled'
    default:
      return 'Active'
  }
}

export const getSortedDashboardListingsByStatus = (listings?: T.ISparseListing[]) => {
  if (!listings) return []

  return listings
    .sort(sortByDateModified)
    .sort((listingA: T.ISparseListing, listingB: T.ISparseListing) => {
      const statusA = getDashboardDisplayStatus(listingA)
      const statusB = getDashboardDisplayStatus(listingB)
      if (statusA === 'Resume Editing' && statusB !== 'Resume Editing') return -1

      if (statusA !== 'Resume Editing' && statusB === 'Resume Editing') return 1

      if (statusA === 'Resume Editing' && statusB === 'Resume Editing') return 0

      if (statusA === 'Closed' && statusB !== 'Closed') return -1

      if (statusA !== 'Closed' && statusB === 'Closed') return 1

      if (statusA === 'Closed' && statusB === 'Closed') return 0

      if (statusA === 'Cancelled' && statusB !== 'Cancelled') return -1

      if (statusA !== 'Cancelled' && statusB === 'Cancelled') return 1

      if (statusA === 'Cancelled' && statusB === 'Cancelled') return 0

      return 0
    })
}

export const getTrafficLightColor = (status: T.IListingStatus | undefined) => {
  if (!status) return 'grey13'

  switch (status.name) {
    case E.ListingStatus.published:
    case E.ListingStatus.listingDetailsUpdated:
    case E.ListingStatus.contractUpdated:
    case E.ListingStatus.inContract:
    case E.ListingStatus.amendmentSent:
    case E.ListingStatus.offerAcceptedNYIC:
    case E.ListingStatus.attorneyReview:
    case E.ListingStatus.offerReceived:
    case E.ListingStatus.newEscrow:
    case E.ListingStatus.escrowClosed:
      return 'green'
    case E.ListingStatus.inProgress:
    case E.ListingStatus.readyForSubmission:
    case E.ListingStatus.needsInput:
    case E.ListingStatus.brokerReview:
    case E.ListingStatus.readyForPublish:
    case 'In Escrow':
    case E.ListingStatus.amendmentNeeded:
    case E.ListingStatus.tempHold:
    case E.ListingStatus.sellerInfoRequested:
    case E.ListingStatus.initialReviewFailed:
      return 'yellow'
    case E.ListingStatus.needsRevision:
    case E.ListingStatus.remove:
    case E.ListingStatus.sold:
    case E.ListingStatus.closedNotPaid:
    case E.ListingStatus.cancellationSigned:
    case E.ListingStatus.cancelled:
    case E.ListingStatus.expired:
    case E.ListingStatus.leased:
      return 'red'
    case E.ListingStatus.draft:
    default:
      return 'grey13'
  }
}

export const getPublicListingSimplifiedStatus = (listingStatus: T.IListingStatus) => {
  switch (listingStatus.name ?? '') {
    case E.ListingStatus.published:
    case E.ListingStatus.listingDetailsUpdated:
    case E.ListingStatus.amendmentNeeded:
    case E.ListingStatus.amendmentSent:
    case E.ListingStatus.offerAcceptedNYIC:
    case E.ListingStatus.offerReceived:
    case E.ListingStatus.cancellationSigned:
    case E.ListingStatus.remove:
      return 'Sale Status: For Sale'
    case E.ListingStatus.attorneyReview:
    case E.ListingStatus.inContract:
    case E.ListingStatus.newEscrow:
    case E.ListingStatus.contractUpdated:
      return 'Sale Status: In Contract'
    case E.ListingStatus.closedNotPaid:
    case E.ListingStatus.sold:
    case E.ListingStatus.leased:
    case E.ListingStatus.escrowClosed:
      return 'Sale Status: Sold'
    default:
      return ''
  }
}

export const getListingCardBarText = (listing: T.IListing | T.ISparseListing) => {
  switch (listing.listingStatus?.name ?? '') {
    case E.ListingStatus.attorneyReview:
    case E.ListingStatus.inContract:
    case E.ListingStatus.newEscrow:
    case E.ListingStatus.contractUpdated:
      return 'IN CONTRACT'
    case E.ListingStatus.leased:
    case E.ListingStatus.closedNotPaid:
    case E.ListingStatus.sold:
    case E.ListingStatus.escrowClosed:
      return 'SOLD'
    default:
      return ''
  }
}

export const getMapSearchForSaleType = (listing: T.IListing | T.ISparseListing) => {
  switch (listing.listingStatus?.name ?? '') {
    case E.ListingStatus.attorneyReview:
    case E.ListingStatus.inContract:
    case E.ListingStatus.newEscrow:
    case E.ListingStatus.contractUpdated:
      return E.MapSearchForSaleType.inContract
    case E.ListingStatus.leased:
    case E.ListingStatus.closedNotPaid:
    case E.ListingStatus.sold:
    case E.ListingStatus.escrowClosed:
      return E.MapSearchForSaleType.sold
    default:
      return E.MapSearchForSaleType.forSale
  }
}
